import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

export async function generarPDFFactura(factura, configuracion, opciones = {}) {
  // Opciones: { formato: 'a4' | 'termica' }
  const formato = opciones.formato || 'a4'
  
  if (formato === 'termica') {
    return generarPDFTermico(factura, configuracion)
  }
  
  const doc = new jsPDF()
  
  const margen = 20
  let y = margen
  
  // ============ CABECERA EMPRESA ============
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  const nombreEmpresa = configuracion?.nombre_empresa || 'Panaderia Hermanos Alonso S.L.'
  doc.text(nombreEmpresa, margen, y)
  y += 6
  
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  
  if (configuracion?.cif) {
    doc.text('CIF: ' + configuracion.cif, margen, y)
    y += 4
  }
  if (configuracion?.direccion) {
    doc.text(configuracion.direccion, margen, y)
    y += 4
  }
  if (configuracion?.cp && configuracion?.poblacion) {
    doc.text(`${configuracion.cp} ${configuracion.poblacion}`, margen, y)
    y += 4
  }
  if (configuracion?.provincia) {
    doc.text(configuracion.provincia, margen, y)
    y += 4
  }
  if (configuracion?.telefono) {
    doc.text('Tel: ' + configuracion.telefono, margen, y)
    y += 4
  }
  if (configuracion?.email) {
    doc.text('Email: ' + configuracion.email, margen, y)
  }
  
  // ============ TÍTULO FACTURA (ARRIBA A LA DERECHA) ============
  const tituloX = 140
  let tituloY = margen
  
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.text('FACTURA', tituloX, tituloY)
  tituloY += 7
  
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.text(`No: ${factura.serie || 'A'}-${factura.numero}`, tituloX, tituloY)
  tituloY += 4
  doc.text(`Fecha: ${new Date(factura.fecha_emision).toLocaleDateString('es-ES')}`, tituloX, tituloY)
  
  // ============ QR CODE (DEBAJO DEL TÍTULO) ============
  if (factura.estado_verifactu === 'enviada' && factura.qr_aeat) {
    try {
      const qrSize = 30
      const qrX = tituloX + 15  // Centrado respecto al título
      const qrY = tituloY + 5   // Debajo del título
      
      doc.addImage(
        factura.qr_aeat,
        'PNG',
        qrX,
        qrY,
        qrSize,
        qrSize
      )
      
      doc.setFontSize(6)
      doc.setFont('helvetica', 'bold')
      doc.text('VERI*FACTU', qrX + (qrSize / 2), qrY + qrSize + 2, { align: 'center' })
    } catch (error) {
      console.error('Error añadiendo QR:', error)
    }
  }
  
  // ============ DATOS CLIENTE ============
  y = margen + 40
  
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.text('CLIENTE', margen, y)
  y += 5
  
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.text(factura.cliente_nombre, margen, y)
  y += 4
  if (factura.cliente_cif) {
    doc.text('CIF: ' + factura.cliente_cif, margen, y)
    y += 4
  }
  if (factura.cliente_direccion) {
    doc.text(factura.cliente_direccion, margen, y)
    y += 4
  }
  
  // ============ TABLA DE LÍNEAS AGRUPADAS POR IVA ============
  y += 4
  
  // Agrupar líneas por IVA
  const lineasPorIva = {}
  ;(factura.lineas || []).forEach(linea => {
    const key = linea.iva_pct
    if (!lineasPorIva[key]) {
      lineasPorIva[key] = []
    }
    lineasPorIva[key].push(linea)
  })
  
  // Generar tabla para cada grupo de IVA
  Object.keys(lineasPorIva).sort((a, b) => Number(b) - Number(a)).forEach(ivaPct => {
    const lineasGrupo = lineasPorIva[ivaPct]
    
    // Consolidar líneas iguales
    const lineasAgrupadas = {}
    lineasGrupo.forEach(linea => {
      const key = `${linea.concepto}-${linea.precio_unit.toFixed(4)}`
      if (lineasAgrupadas[key]) {
        lineasAgrupadas[key].cantidad += linea.cantidad
        lineasAgrupadas[key].base += linea.base
      } else {
        lineasAgrupadas[key] = { ...linea }
      }
    })
    
    // ⭐ CRÍTICO: Mostrar precio NETO (precio_unit) y base NETA
    const lineasParaTabla = Object.values(lineasAgrupadas).map(l => [
      l.concepto,
      l.cantidad.toFixed(2),
      l.precio_unit.toFixed(4) + ' EUR',  // ✅ Este es el precio NETO
      l.base.toFixed(2) + ' EUR'           // ✅ Esta es la base NETA
    ])
    
    autoTable(doc, {
      startY: y,
      head: [[`Concepto (IVA ${ivaPct}%)`, 'Cantidad', 'Precio/ud (NETO)', 'Base']],
      body: lineasParaTabla,
      theme: 'grid',
      headStyles: { 
        fillColor: [45, 212, 191],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 9
      },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { halign: 'center', cellWidth: 30 },
        2: { halign: 'right', cellWidth: 35 },
        3: { halign: 'right', cellWidth: 25 }
      }
    })
    
    y = doc.lastAutoTable.finalY + 2
  })
  
  // ============ TOTALES CON DESGLOSE DE IVA ============
  y += 8
  const xLabel = 120
  const xValor = 185
  
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  
  // Desglose por tipo de IVA
  if (factura.desglose_iva && factura.desglose_iva.length > 0) {
    factura.desglose_iva.forEach(tramo => {
      doc.setFont('helvetica', 'bold')
      doc.text(`IVA ${tramo.iva_pct}%:`, margen, y)
      doc.setFont('helvetica', 'normal')
      y += 5
      
      doc.text(`Base ${tramo.iva_pct}%:`, xLabel, y)
      doc.text(tramo.base.toFixed(2) + ' EUR', xValor, y, { align: 'right' })
      y += 4
      
      doc.text(`IVA ${tramo.iva_pct}%:`, xLabel, y)
      doc.text(tramo.iva.toFixed(2) + ' EUR', xValor, y, { align: 'right' })
      y += 4
      
      if (tramo.re > 0) {
        doc.text(`R.E. ${tramo.re_pct}%:`, xLabel, y)
        doc.text(tramo.re.toFixed(2) + ' EUR', xValor, y, { align: 'right' })
        y += 4
      }
      
      y += 2
    })
    
    doc.setLineWidth(0.3)
    doc.line(xLabel, y, xValor, y)
    y += 5
  }
  
  // TOTALES FINALES
  doc.text('Base imponible:', xLabel, y)
  doc.text(factura.base_imponible.toFixed(2) + ' EUR', xValor, y, { align: 'right' })
  y += 5
  
  doc.text('IVA total:', xLabel, y)
  doc.text(factura.iva.toFixed(2) + ' EUR', xValor, y, { align: 'right' })
  y += 5
  
  if (factura.recargo_equivalencia > 0) {
    doc.text('Recargo total:', xLabel, y)
    doc.text(factura.recargo_equivalencia.toFixed(2) + ' EUR', xValor, y, { align: 'right' })
    y += 5
  }
  
  doc.setLineWidth(0.5)
  doc.line(xLabel, y, xValor, y)
  y += 5
  
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text('TOTAL:', xLabel, y)
  doc.text(factura.total.toFixed(2) + ' EUR', xValor, y, { align: 'right' })
  
  // ============ CSV VERIFACTU ============
  if (factura.estado_verifactu === 'enviada' && factura.csv_aeat) {
    y += 8
    
    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    doc.text(`CSV: ${factura.csv_aeat.substring(0, 40)}...`, margen, y)
    y += 3
    doc.text('Factura verificable en sede.agenciatributaria.gob.es', margen, y)
  }
  
  // ============ FOOTER ============
  const pageHeight = doc.internal.pageSize.height
  doc.setFontSize(7)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100)
  
  const footerText = factura.estado_verifactu === 'enviada' 
    ? 'Factura electronica enviada a VeriFactu - AEAT'
    : 'Factura pendiente de envio a VeriFactu - AEAT'
  
  doc.text(footerText, 105, pageHeight - 10, { align: 'center' })
  
  return doc
}

// ============ NUEVO: PDF PARA IMPRESORA TÉRMICA 80mm ============
function generarPDFTermico(factura, configuracion) {
  // Ancho de 80mm = 226 píxeles en 72 DPI
  // Alto dinámico según contenido
  const anchoMM = 80
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [anchoMM, 297] // Alto temporal, se ajustará
  })
  
  const margen = 5
  let y = margen
  const anchoUtil = anchoMM - (margen * 2)
  
  // ============ CABECERA EMPRESA (CENTRADA) ============
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  const nombreEmpresa = configuracion?.nombre_empresa || 'Panaderia Hermanos Alonso S.L.'
  doc.text(nombreEmpresa, anchoMM / 2, y, { align: 'center' })
  y += 5
  
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  
  if (configuracion?.cif) {
    doc.text('CIF: ' + configuracion.cif, anchoMM / 2, y, { align: 'center' })
    y += 3.5
  }
  if (configuracion?.direccion) {
    doc.text(configuracion.direccion, anchoMM / 2, y, { align: 'center' })
    y += 3.5
  }
  if (configuracion?.telefono) {
    doc.text('Tel: ' + configuracion.telefono, anchoMM / 2, y, { align: 'center' })
    y += 3.5
  }
  
  // ============ SEPARADOR ============
  y += 2
  doc.setLineWidth(0.3)
  doc.line(margen, y, anchoMM - margen, y)
  y += 4
  
  // ============ FACTURA ============
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.text('FACTURA', anchoMM / 2, y, { align: 'center' })
  y += 5
  
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.text(`${factura.serie || 'A'}-${factura.numero}`, anchoMM / 2, y, { align: 'center' })
  y += 4
  doc.text(new Date(factura.fecha_emision).toLocaleDateString('es-ES'), anchoMM / 2, y, { align: 'center' })
  y += 5
  
  // ============ CLIENTE ============
  doc.setLineWidth(0.3)
  doc.line(margen, y, anchoMM - margen, y)
  y += 4
  
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.text('CLIENTE:', margen, y)
  y += 4
  
  doc.setFont('helvetica', 'normal')
  doc.text(factura.cliente_nombre, margen, y)
  y += 3.5
  if (factura.cliente_cif) {
    doc.text('CIF: ' + factura.cliente_cif, margen, y)
    y += 3.5
  }
  
  // ============ LÍNEAS ============
  y += 2
  doc.setLineWidth(0.3)
  doc.line(margen, y, anchoMM - margen, y)
  y += 4
  
  doc.setFontSize(8)
  doc.setFont('helvetica', 'bold')
  doc.text('DETALLE', margen, y)
  y += 4
  
  doc.setFont('helvetica', 'normal')
  
  // Agrupar y mostrar líneas
  const lineasPorIva = {}
  ;(factura.lineas || []).forEach(linea => {
    const key = linea.iva_pct
    if (!lineasPorIva[key]) {
      lineasPorIva[key] = []
    }
    lineasPorIva[key].push(linea)
  })
  
  Object.keys(lineasPorIva).sort((a, b) => Number(b) - Number(a)).forEach(ivaPct => {
    const lineasGrupo = lineasPorIva[ivaPct]
    
    doc.setFont('helvetica', 'bold')
    doc.text(`IVA ${ivaPct}%:`, margen, y)
    y += 3.5
    doc.setFont('helvetica', 'normal')
    
    lineasGrupo.forEach(linea => {
      // Línea 1: Concepto y cantidad
      doc.text(`${linea.concepto} x${linea.cantidad}`, margen, y)
      y += 3
      
      // Línea 2: Precio y base
      const precioTexto = `  ${linea.precio_unit.toFixed(4)}€/ud`
      const baseTexto = `${linea.base.toFixed(2)}€`
      doc.text(precioTexto, margen + 2, y)
      doc.text(baseTexto, anchoMM - margen, y, { align: 'right' })
      y += 4
    })
    
    y += 1
  })
  
  // ============ TOTALES ============
  y += 2
  doc.setLineWidth(0.3)
  doc.line(margen, y, anchoMM - margen, y)
  y += 4
  
  doc.setFontSize(8)
  
  // Desglose por IVA
  if (factura.desglose_iva && factura.desglose_iva.length > 0) {
    factura.desglose_iva.forEach(tramo => {
      doc.setFont('helvetica', 'bold')
      doc.text(`IVA ${tramo.iva_pct}%:`, margen, y)
      y += 3.5
      
      doc.setFont('helvetica', 'normal')
      doc.text(`  Base:`, margen + 2, y)
      doc.text(tramo.base.toFixed(2) + '€', anchoMM - margen, y, { align: 'right' })
      y += 3
      
      doc.text(`  IVA:`, margen + 2, y)
      doc.text(tramo.iva.toFixed(2) + '€', anchoMM - margen, y, { align: 'right' })
      y += 3
      
      if (tramo.re > 0) {
        doc.text(`  R.E.:`, margen + 2, y)
        doc.text(tramo.re.toFixed(2) + '€', anchoMM - margen, y, { align: 'right' })
        y += 3
      }
      
      y += 1
    })
  }
  
  y += 2
  doc.setLineWidth(0.5)
  doc.line(margen, y, anchoMM - margen, y)
  y += 5
  
  // TOTAL
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('TOTAL:', margen, y)
  doc.text(factura.total.toFixed(2) + ' EUR', anchoMM - margen, y, { align: 'right' })
  y += 6
  
  // ============ QR CODE (CENTRADO) ============
  if (factura.estado_verifactu === 'enviada' && factura.qr_aeat) {
    try {
      y += 2
      const qrSize = 35
      const qrX = (anchoMM - qrSize) / 2
      
      doc.addImage(
        factura.qr_aeat,
        'PNG',
        qrX,
        y,
        qrSize,
        qrSize
      )
      y += qrSize + 2
      
      doc.setFontSize(7)
      doc.setFont('helvetica', 'bold')
      doc.text('VERI*FACTU', anchoMM / 2, y, { align: 'center' })
      y += 4
    } catch (error) {
      console.error('Error añadiendo QR:', error)
    }
  }
  
  // ============ CSV ============
  if (factura.estado_verifactu === 'enviada' && factura.csv_aeat) {
    y += 2
    doc.setFontSize(6)
    doc.setFont('helvetica', 'normal')
    
    // Dividir CSV en líneas
    const csvCorto = factura.csv_aeat.substring(0, 32)
    doc.text(`CSV: ${csvCorto}...`, anchoMM / 2, y, { align: 'center' })
    y += 3
    doc.text('Verificable en:', anchoMM / 2, y, { align: 'center' })
    y += 3
    doc.text('sede.agenciatributaria.gob.es', anchoMM / 2, y, { align: 'center' })
    y += 4
  }
  
  // ============ FOOTER ============
  y += 2
  doc.setFontSize(6)
  doc.setTextColor(100)
  const footerText = factura.estado_verifactu === 'enviada' 
    ? 'Factura enviada a VeriFactu - AEAT'
    : 'Pendiente de envio a VeriFactu'
  doc.text(footerText, anchoMM / 2, y, { align: 'center' })
  y += 10 // Margen final
  
  return doc
}

export async function guardarPDFEnSupabase(doc, factura, supabase) {
  const pdfBlob = doc.output('blob')
  const fileName = `factura-${factura.serie}-${factura.numero}.pdf`
  
  const { data, error } = await supabase.storage
    .from('facturas-pdf')
    .upload(fileName, pdfBlob, {
      contentType: 'application/pdf',
      upsert: true
    })
  
  if (error) throw error
  
  const { data: urlData } = supabase.storage
    .from('facturas-pdf')
    .getPublicUrl(fileName)
  
  return urlData.publicUrl
}
